import { useMemo } from 'react'
import { useFormContext } from 'react-hook-form'
import FilterListV2, { TFilterItem } from '@/components/content/FilterListV2'
import { Select } from '@/components/content/Select'
import { Utils } from '@/utils/Utils'
import { useGetPriceCodes } from '@/services/settlement/settlementPricing/useSettlementPricingQueries'
import { useGetCountries } from '@/services/commonCode/useCommonCodeQueries'
import { ORDER_TYPE_OPTIONS, UNIT_OPTIONS } from '../../consts'
import { BasePriceListSchemaType, getInitForm } from '../../schemas/basePriceListSchema'

interface BasePriceFormProps {
  onSearch: (formValues: BasePriceListSchemaType) => void
  isLoading?: boolean
}

export function BasePriceForm({ onSearch, isLoading }: BasePriceFormProps) {
  const { watch, setValue, handleSubmit, reset } = useFormContext<BasePriceListSchemaType>()
  const values = watch()
  const { data: codes } = useGetPriceCodes()
  const { data: countries } = useGetCountries() // 국가: moms BE 원천

  // 빈 문자열 옵션은 layer Select 에서 금지(빈 값=선택해제). "전체"는 placeholder + onClear 로 표현.
  const countryOptions = useMemo(() => Utils.createSelectOptions(countries ?? [], 'name', 'code'), [countries])
  const serviceOptions = useMemo(() => Utils.createSelectOptions(codes?.serviceTypes ?? [], 'name', 'code'), [codes?.serviceTypes])

  const filterList = useMemo<TFilterItem<BasePriceListSchemaType>[]>(
    () => [
      {
        field: 'country',
        label: '국가',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 140 }}>
            <Select
              value={values.country ?? undefined}
              onValueChange={(v) => setValue('country', v)}
              onClear={() => setValue('country', null)}
              options={countryOptions}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '국가'}
            />
          </div>
        ),
        fieldWidth: 140,
      },
      {
        field: 'serviceType',
        label: '서비스 항목',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 180 }}>
            <Select
              value={values.serviceType ?? undefined}
              onValueChange={(v) => setValue('serviceType', v)}
              onClear={() => setValue('serviceType', null)}
              options={serviceOptions}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '서비스 항목'}
            />
          </div>
        ),
        fieldWidth: 180,
      },
      {
        field: 'unitType',
        label: '단위',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 120 }}>
            <Select
              value={values.unitType ?? undefined}
              onValueChange={(v) => setValue('unitType', v)}
              onClear={() => setValue('unitType', null)}
              options={UNIT_OPTIONS}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '단위'}
            />
          </div>
        ),
        fieldWidth: 120,
      },
      {
        field: 'orderType',
        label: '주문유형',
        // eslint-disable-next-line react/no-unstable-nested-components
        component: ({ isOnPanel }) => (
          <div style={{ width: isOnPanel ? '100%' : 120 }}>
            <Select
              value={values.orderType ?? undefined}
              onValueChange={(v) => setValue('orderType', v)}
              onClear={() => setValue('orderType', null)}
              options={ORDER_TYPE_OPTIONS}
              placeholder="전체"
              floatingBlockLabel={isOnPanel ? undefined : '주문유형'}
            />
          </div>
        ),
        fieldWidth: 120,
      },
    ],
    [values.country, values.serviceType, values.unitType, values.orderType, countryOptions, serviceOptions, setValue],
  )

  return (
    <FilterListV2<BasePriceListSchemaType>
      list={filterList}
      onSearch={handleSubmit(onSearch)}
      onReset={() => reset(getInitForm())}
      isSubmitting={isLoading}
      initialValues={getInitForm()}
    />
  )
}
